import "./EntryGroupTable.less";

import { notification, Badge, Button, Input, Tooltip } from "antd";
import ButtonGroup from "antd/lib/button/button-group";
import { ColumnProps } from "antd/lib/table";
import { Guid } from "guid-typescript";
import { cloneDeep, isEqual, merge } from "lodash";
import { curry, __ } from "lodash/fp";
import React from "react";
import Highlighter from "react-highlight-words";
import { Link } from "react-router-dom";

import { OrderedEntryApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { IBaseProps, IHighlightProps } from "lib/data/interfaces";
import { DataFieldTemplate, DataFieldValue, EntryGroup, OrderedDataFieldTemplate, OrderedDataFieldValue, OrderedEntry, Permission, Requirement, Section } from "lib/data/model";
import { DataFieldTemplateService, PermissionService, StateService, UtilityService } from "lib/services";
import { LoginService } from "lib/services";

import { AuthorizedComponent, DataItem, SortableTable } from "components/general";
import { EntryPage } from "components/pages";
import { Requiredness } from "lib/data/model/enumerations";

interface IEntryGroupTableProps extends IBaseProps, IHighlightProps {
	section: Section;
	entryGroup: EntryGroup;
	requiredPermission?: Permission;
	isEditable?: boolean;
	onSave: (entryGroup: EntryGroup) => Promise<boolean>;
	onComplete: (entryGroup: EntryGroup) => void;
	footer?: ((currentPageData: OrderedEntry[]) => React.ReactNode) | React.ReactNode;
}

interface IEntryGroupTableState {
	columns: Array<ColumnProps<OrderedEntry>>;
	isTableLoading: boolean;
	columnEditId?: number;
	columnEditLabelValue?: string;
}

interface IRowState {
	isEditing: boolean;
	isSaving: boolean;
}

interface IRowDetail {
	key: string;
	state: IRowState;
	values: Map<Guid, null | DataFieldValue | undefined>;
}

export class EntryGroupTable extends React.Component<IEntryGroupTableProps, IEntryGroupTableState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	private entryGroup!: EntryGroup;
	private permission?: Permission;

	private rowDetails: Map<OrderedEntry, IRowDetail> = new Map<OrderedEntry, IRowDetail>();

	constructor(props: IEntryGroupTableProps) {

		super(props);

		this.state = {
			columns: [],
			isTableLoading: false
		};
	}

	public async componentWillMount() {

		// Store Incoming Entry Group
		this.entryGroup = cloneDeep(this.props.entryGroup);

		// Check Entries
		if (this.entryGroup.entries === undefined) {

			// Initialize Array
			this.entryGroup.entries = [];
		}

		// Set Permission
		this.permission = this.props.requiredPermission;

		// Rebuild Columns
		const columns = await this.defineColumns(this.props.isEditable);

		// Set Columns
		StateService.setCancelableState(() => this.setState({ columns }), this.apiCancellationToken);
	}

	public async componentWillReceiveProps(newProps: IEntryGroupTableProps) {

		// Store Incoming Entry Group
		this.entryGroup = cloneDeep(newProps.entryGroup);

		// Check Entries
		if (this.entryGroup.entries === undefined) {

			// Initialize Array
			this.entryGroup.entries = [];
		}

		// Check For Different Permission
		if (!isEqual(this.permission, newProps.requiredPermission)) {

			// Set Permission
			this.permission = newProps.requiredPermission;
		}

		// Rebuild Columns
		const columns = await this.defineColumns(newProps.isEditable);

		// Check For Different Columns
		if (!isEqual(columns, this.state.columns)) {

			// Set Columns
			StateService.setCancelableState(() => this.setState({ columns }), this.apiCancellationToken);
		}
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {

		// Define Class Name
		const className = [
			"entry-group-table",
			this.props.className
		].filter((c) => c !== undefined).join(" ").trim();

		// Get Footer
		const footer = this.getFooter();

		return (
			<SortableTable<OrderedEntry>
				rowKey={this.generateRowKey}
				className={className}
				rowClassName={this.getRowClass}
				size="small"
				pagination={false}
				columns={this.state.columns}
				dataSource={this.entryGroup.entries}
				footer={footer}
				sortDisabled={!this.props.isEditable}
				onSortEndDiff={this.onEntryRecordSort}
				loading={this.state.isTableLoading}
			/>
		);
	}

	private onEntryRecordSort = async (oldSort: OrderedEntry[], newSort: OrderedEntry[]) => {

		// If the record was moved from it's original position, save the entry group
		if (!isEqual(oldSort, newSort)) {

			const recordSavePromises: Array<Promise<OrderedEntry | undefined>> = [];
			StateService.setCancelableState(() => this.setState({ isTableLoading: true }), this.apiCancellationToken);

			// Render the desired order
			this.entryGroup.entries = newSort;
			this.forceUpdate();

			for (let i = 0; i < newSort.length; i++) {

				const entry = newSort[i];

				entry.order = i;

				// Save Ordered Entry
				recordSavePromises.push(OrderedEntryApi.save(this.props.section, this.entryGroup, entry));
			}

			let sortSuccessful = false;

			try {

				// await bulk save of records
				const orderedEntry = await UtilityService.asyncTimeout(Promise.all(recordSavePromises));
				if (orderedEntry !== undefined) {

					sortSuccessful = true;
				}
			}
			catch (error) {

				// If there was an issue saving, revert to the old order of records
				this.entryGroup.entries = oldSort;
				this.forceUpdate();
			}

			if (sortSuccessful === true) {

				notification.success({
					message: "Successfully reordered records"
				});
			}
			else {

				notification.error({
					message: "Failed to reorder records"
				});
			}

			StateService.setCancelableState(() => this.setState({ isTableLoading: false }), this.apiCancellationToken);
		}
	}

	private getRowClass = (record: OrderedEntry): string => {

		// Get Row State
		const rowDetail = this.rowDetails.get(record);

		// Check Row State
		if (rowDetail !== undefined) {

			// Check For Editing
			if (rowDetail.state.isEditing) {

				// Return Class Name
				return "entry-grid-form-row";
			}
		}

		// Return Nothing
		return "entry-grid-row";
	}

	private getFooter = (): (((rows: object[]) => React.ReactNode) | undefined) => {

		// Check Footer
		if (this.props.footer === undefined) {

			// Return Nothing
			return undefined;
		}
		else if (typeof this.props.footer === "function") {

			// Cast Function
			const footer = this.props.footer! as (rows: OrderedEntry[]) => React.ReactNode;

			// Return Function
			return (rows: object[]) => footer(rows as OrderedEntry[]) as React.ReactNode;
		}
		else {

			// Return New Function
			return () => this.props.footer;
		}
	}

	private isUserPermitted = async (): Promise<boolean> => {

		// Check For Required Permission
		if (this.permission) {

			// Get Logged In User
			const loggedInUser = await LoginService.getUserIfExists();

			// Check Logged In User
			if (loggedInUser) {

				// Request Permission
				const isPermitted = await PermissionService.userHasPermission(loggedInUser, this.permission);

				// Check Permission
				if (isPermitted) {

					// Indicate User Is Permitted
					return true;
				}
			}
		}
		else {

			// Indicate User Is Permitted
			return true;
		}

		// Indicate User Is NOT Permitted
		return false;
	}

	private onColumnRenameSave = async () => {

		const clonedEntryGroup = cloneDeep(this.entryGroup);
		const columnIndex = clonedEntryGroup.columns.findIndex((column) => column.item.field.id === this.state.columnEditId);

		if (columnIndex !== undefined) {
			clonedEntryGroup.columns[columnIndex].item.field.name = this.state.columnEditLabelValue || "";
		}

		await this.props.onSave(clonedEntryGroup);

		this.setState({
			columnEditId: undefined,
			columnEditLabelValue: undefined
		}, async () => {

			const columns = await this.defineColumns(true);

			this.setState({ columns });
		});
	}

	private onColumnRename = (_event: React.MouseEvent, columnId?: number) => {

		const matchingColumn = this.entryGroup.columns.find((column) => column.item.field.id === columnId);

		this.setState({
			columnEditId: columnId,
			columnEditLabelValue: matchingColumn ? matchingColumn.item.field.name : ""
		}, async () => {

			const columns = await this.defineColumns(true);

			this.setState({
				columns
			});
		});
	}

	private onColumnDelete = (_event: React.MouseEvent, fieldId: number) => {

		// Get Column
		const columnIndex = this.entryGroup.columns.findIndex((column) => column.item.field.id === fieldId);

		// Ensure Column Was Found
		if (columnIndex > -1) {

			// Remove Column From Object
			this.entryGroup.columns.splice(columnIndex, 1);

			// Save Change
			this.props.onSave(this.entryGroup);
		}
	}

	private getColumnTitle = (template: DataFieldTemplate, isEditable?: boolean, isRequired = false): React.ReactNode => {

		if (isEditable) {

			return (
				<AuthorizedComponent
					notAuthorizedChildren={template.field.name}
				>
					{
						this.state.columnEditId === template.field.id
							?
							(
								<Input
									defaultValue={this.state.columnEditLabelValue}
									onChange={event => this.setState({ columnEditLabelValue: event.target.value })}
									size="small"
								/>
							)
							:
							(
								isRequired
									?
									(
										<Badge offset={[17, 0]} count={<div className="entry-group-table-required-addable-column">required</div>}>
											{template.field.name}
										</Badge>
									)
									:
									template.field.name
							)
					}
					{
						this.state.columnEditId === template.field.id
							?
							(
								<ButtonGroup className="column-button-group">
									<Tooltip title="Save Column Name">
										<Button
											icon="check"
											size="small"
											onClick={this.onColumnRenameSave}
										/>
									</Tooltip>
									<Tooltip title="Cancel Column Rename">
										<Button
											icon="close"
											size="small"
											type="danger"
											onClick={curry(this.onColumnRename)(__, undefined)}
										/>
									</Tooltip>
								</ButtonGroup>
							)
							:
							(
								<ButtonGroup className="column-button-group">
									{/* <Tooltip title="Edit column name">
										<Button
											icon="edit"
											size="small"
											onClick={curry(this.onColumnRename)(__, template.field.id)}
										/>
									</Tooltip> */}
									<Tooltip title={`Delete the column "${template.field.name}"`}>
										<Button
											icon="delete"
											size="small"
											type="danger"
											onClick={curry(this.onColumnDelete)(__, template.field.id)}
										/>
									</Tooltip>
								</ButtonGroup>
							)
					}
				</AuthorizedComponent>
			);
		}

		return template.field.name;
	}

	private defineColumns = async (isEditable?: boolean): Promise<Array<ColumnProps<OrderedEntry>>> => {

		// Loop Through Records
		for (const orderedListing of this.entryGroup.entries) {

			// Initialize Row Detail
			const rowDetail: IRowDetail = {
				key: this.generateRowKey(orderedListing),
				state: {
					isEditing: false,
					isSaving: false
				},
				values: new Map<Guid, DataFieldValue | undefined>()
			};

			// Set Row Detail
			this.rowDetails.set(orderedListing, rowDetail);
		}

		// Initialize Table Columns
		const columns: Array<ColumnProps<OrderedEntry>> = [
			{
				title: "Name",
				key: "name",
				render: (orderedEntry: OrderedEntry) => (
					<Link to={`${EntryPage.BasePath}/${orderedEntry.item.id}`} className="entry-link">
						{(this.props.highlightWords
							?
							<Highlighter searchWords={[this.props.highlightWords.join(" ")]} textToHighlight={orderedEntry.item.name} />
							:
							orderedEntry.item.name
						)}
					</Link>
				),
			}
		];

		// Get Sorted Data Field Templates
		const entryGroupTemplates = this.entryGroup.columns.sort(OrderedDataFieldTemplate.sort);

		// Loop Through Listing Fields
		// (Use Only Template Portion Of Ordered Template)
		for (const template of entryGroupTemplates.map((o) => o.item)) {

			// Check if column should be displayed as required
			let isRequired = false;

			if (this.props.isEditable) {

				isRequired = DataFieldTemplateService.isDataFieldRequired(this.entryGroup.template.requirements, template.field);
			}

			// Generate Column ID
			const columnId: Guid = Guid.create();

			// Loop Through Records
			for (const orderedEntry of this.entryGroup.entries) {

				// Get Row Detail
				const rowDetail = this.rowDetails.get(orderedEntry)!;

				// Add Column To Row Detail
				rowDetail.values.set(columnId, null);
			}

			// Add Field As Entry Table Column
			columns.push({
				title: this.getColumnTitle(template, isEditable, isRequired),
				key: template.field.name.toLocaleLowerCase(),
				render: (orderedEntry: OrderedEntry) => {

					// Get Row Detail
					const rowDetail = this.rowDetails.get(orderedEntry);

					// Check Row Detail
					if (rowDetail !== undefined) {

						// Check Ordered Entry's Data
						if (orderedEntry.data === undefined) {

							// Initialize Array
							orderedEntry.data = [];
						}

						// Get Entry's Field Value
						let orderedValue = orderedEntry.data.find((fieldValue) => fieldValue.item.template.field.id === template.field.id);

						// Initialize Column Requirement
						let columnRequirement: Requirement | undefined;

						// Check For Entry Group Template
						if (this.entryGroup.template !== undefined && this.entryGroup.template.requirements !== undefined) {

							// Get Requirement Covering Column
							columnRequirement = DataFieldTemplateService.getRequirementFromTemplate(this.entryGroup.template.requirements, template);
						}

						// Get Listing's Updated Field Value
						const updatedValue = rowDetail.values.get(columnId);

						// Check Updated Value
						if (updatedValue !== null && updatedValue !== undefined) {

							// Check Old Value
							if (orderedValue !== undefined) {

								// Replace Value With New Value
								orderedValue.item = updatedValue;
							}
							else {

								// Create New Ordered Value
								orderedValue = new OrderedDataFieldValue();
								orderedValue.order = 0;
								orderedValue.item = updatedValue;
							}
						}

						// Return Formatted Data Item
						return (

							<DataItem
								template={template}
								value={(orderedValue ? orderedValue.item : undefined)}
								showTitle={false}
								highlightWords={this.props.highlightWords}
								requiredPermission={this.permission}
								isEditable={rowDetail.state.isEditing}
								isEditing={rowDetail.state.isEditing}
								isSaving={rowDetail.state.isSaving}
								onSave={curry(this.onValueSave)(__, columnId, orderedEntry)}
								isRequired={(columnRequirement !== undefined ? (columnRequirement.requiredness === Requiredness.REQUIRED ? true : false) : false)}
							/>
						);
					}
					else {

						// Return Nothing
						return null;
					}
				}
			});
		}

		// Check User Permission
		const isPermitted = await this.isUserPermitted();

		// Check For Permission & Editability
		if (isPermitted && isEditable) {

			// Add Secure Components As Column
			columns.push({

				key: "actions",
				width: "100px",
				align: "center",
				render: (orderedEntry: OrderedEntry) => {

					// Get Row State
					const rowDetail = this.rowDetails.get(orderedEntry);

					// Check Row State
					if (rowDetail !== undefined) {

						// Check For Editing
						if (rowDetail.state.isEditing) {

							// Return Save & Cancel Button
							return (
								<Button.Group>
									<Button
										icon="check"
										loading={rowDetail.state.isSaving}
										disabled={rowDetail.state.isSaving}
										onClick={curry(this.onTriggerSave)(__, orderedEntry)}
									/>

									<Button
										icon="close"
										disabled={rowDetail.state.isSaving}
										onClick={curry(this.onEditCancel)(__, orderedEntry)}
									/>
								</Button.Group>
							);
						}
					}

					// Return Edit & Delete Buttons
					return (
						<Button.Group>
							<Button
								icon="edit"
								onClick={curry(this.onEdit)(__, orderedEntry)}
							/>

							<Button
								icon="delete"
								onClick={curry(this.onDelete)(__, orderedEntry)}
							/>
						</Button.Group>
					);
				}
			});
		}

		// Return Built Columns
		return columns;
	}

	private onEdit = (_event: React.MouseEvent, record: OrderedEntry) => {

		// Get Row State
		const rowDetail = this.rowDetails.get(record);

		// Check Row State
		if (rowDetail !== undefined) {

			// Check For Editing
			if (!rowDetail.state.isEditing) {

				// Change Editing State
				rowDetail.state.isEditing = true;

				// Refresh Page
				this.forceUpdate();
			}
		}
	}

	private onEditCancel = (_event: React.MouseEvent, record: OrderedEntry) => {

		// Get Row State
		const rowDetail = this.rowDetails.get(record);

		// Check Row State
		if (rowDetail !== undefined) {

			// Check For Editing
			if (rowDetail.state.isEditing) {

				// Change Editing State
				rowDetail.state.isEditing = false;

				// Refresh Page
				this.forceUpdate();
			}
		}
	}

	private onTriggerSave = (_event: React.MouseEvent, record: OrderedEntry) => {

		// Get Row State
		const rowDetail = this.rowDetails.get(record);

		// Check Row State
		if (rowDetail !== undefined && rowDetail.state.isEditing) {

			// Loop Through Values
			rowDetail.values.forEach((_value, key) => {

				// Reset Back To Null
				rowDetail.values.set(key, null);
			});

			// Change Saving State
			rowDetail.state.isSaving = true;

			// Refresh Page
			this.forceUpdate();
		}
	}

	private onValueSave = async (item: DataFieldValue | undefined, columnId: Guid, record: OrderedEntry) => {

		// Get Row State
		const rowDetail = this.rowDetails.get(record);

		// Check Row Value
		if (rowDetail !== undefined) {

			// Add Item To Row Value
			rowDetail.values.set(columnId, item);

			// Ensure We're Finished With Saving
			if (!Array.from(rowDetail.values.values()).some((value) => value === null)) {

				// Check For Any Failed Values
				if (Array.from(rowDetail.values.values()).some((value) => value === undefined)) {

					// Update Row State
					rowDetail.state.isSaving = false;

					// Refresh Page
					this.forceUpdate();
				}
				else {

					// Save Record
					await this.onSave(record);
				}
			}
		}
	}

	private onSave = async (record: OrderedEntry) => {

		// Get Row State
		const rowDetail = this.rowDetails.get(record);

		// Check Row Value
		if (rowDetail !== undefined) {

			// Clone Entry
			const clonedEntry = cloneDeep(record);

			// Create Flags Indicating If Records Were Modified
			let isOrderedEntryModified = false;

			// Loop Through New Values
			rowDetail.values.forEach((value) => {

				// Use Value As DataFieldValue
				const dataFieldValue = value as DataFieldValue;

				// Initialize Saving Value
				let savingValue: OrderedDataFieldValue;

				// Construct New Value
				savingValue = new OrderedDataFieldValue();
				savingValue.order = 0;
				savingValue.item = dataFieldValue;

				// Check For Value ID
				if (dataFieldValue.id !== undefined) {

					// Find Existing Data Field Value
					const existingValueIndex = clonedEntry.data.findIndex((v) => v.item.id === dataFieldValue.id);

					// Check Existing Value
					if (existingValueIndex !== -1) {

						// Store Existing Value
						savingValue = clonedEntry.data[existingValueIndex];
						savingValue.item = dataFieldValue;

						// Replace Existing Value
						clonedEntry.data[existingValueIndex] = savingValue;
					}
					else {

						// Add New Value
						clonedEntry.data.push(savingValue);
					}
				}
				else {

					// Add New Value
					clonedEntry.data.push(savingValue);
				}

				// Indicate Ordered Entry Was Modified
				isOrderedEntryModified = true;
			});

			// Clone Entry Group
			const clonedEntryGroup = cloneDeep(this.props.entryGroup);

			// Initialize Successful Save Flag
			let isSaveSuccessful = false;

			// Check For Need To Save Ordered Entry
			if (isOrderedEntryModified) {

				// Save Ordered Entry
				const savedOrderedEntry = await OrderedEntryApi.save(this.props.section, this.entryGroup, clonedEntry);

				// Check Saved Ordered Entry
				if (savedOrderedEntry !== undefined) {

					// Indicate Save Was Successful
					isSaveSuccessful = true;

					// Merge Changes Back Onto Original Record
					merge(clonedEntry, savedOrderedEntry);
				}
				else {

					// Indicate Save Was NOT Successful
					isSaveSuccessful = false;
				}
			}

			// Look For Matching Ordered Entry
			const matchingEntryIndex = clonedEntryGroup.entries.findIndex((entry) => entry.id === clonedEntry.id);

			if (matchingEntryIndex !== -1) {

				clonedEntryGroup.entries.splice(matchingEntryIndex, 1, clonedEntry);
			}
			else {

				clonedEntryGroup.entries.push(clonedEntry);
			}

			// Check For Successful Save
			if (isSaveSuccessful) {

				// Trigger Save Handler
				this.props.onSave(clonedEntryGroup);

				notification.success({
					message: "Successfully saved record"
				});

				// Update Row State
				rowDetail.state.isEditing = false;
				rowDetail.state.isSaving = false;
			}
			else {

				notification.error({
					message: "Failed to save record"
				});

				// Update Row State
				rowDetail.state.isSaving = false;
			}

			// Refresh Page
			this.forceUpdate();
		}
	}

	private onDelete = async (_event: React.MouseEvent, record: OrderedEntry) => {

		// Find Existing Record In Entry Group
		const existingEntryIndex = this.entryGroup.entries.findIndex((existingEntry) => existingEntry === record);

		// Check Existing Entry
		if (existingEntryIndex !== -1) {

			// Store Entry To Delete
			const entryToDelete = this.entryGroup.entries[existingEntryIndex];

			// Delete Entry
			const isDeleted = await OrderedEntryApi.delete(this.props.section, this.entryGroup, entryToDelete);

			// Check Deletion
			if (isDeleted) {

				// Remove Entry From Group
				this.entryGroup.entries.splice(existingEntryIndex, 1);

				notification.success({
					message: "Successfully deleted record"
				});
			}
			else {

				notification.error({
					message: "Failed to delete the record"
				});
			}

			// Complete Removal
			this.props.onComplete(this.entryGroup);
		}
	}

	private generateRowKey = (record: OrderedEntry): string => {

		// Get Existing Record Edit State
		const rowDetail = this.rowDetails.get(record);

		// Check Existing Edit State
		if (rowDetail === undefined) {

			// Get Record ID
			const rowKey: string = (record.item.id !== undefined ? record.item.id : Guid.create()).toString();

			// Return New Key
			return rowKey;
		}
		else {

			// Return Current Key
			return rowDetail.key;
		}
	}
}
